import { Injectable } from '@angular/core';
import { Socket } from 'ngx-socket-io';
import { EnvironmentService } from '../services/environment.service';
import { AuthService } from '../services/auth.service';
import { Observable, Subject } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { WSImport } from './import.models';

export type ImporterRegistrationStatus = 'connected'|'disconnected'|'authenticated'|'error';

export class ImporterRegistration {
    private _socket:Socket = null;

    status:ImporterRegistrationStatus = 'disconnected'

    constructor(private environmentService:EnvironmentService, private authService:AuthService) {
        this._socket = new Socket({ 
            url: this.environmentService.environment.ImporterURL, 
            options: {} 
        })

        this._socket.on('connect', () => { 
            console.log('Importer connect')

            this.status = 'connected';

            this.authService.getAuthToken().then((token) => {
                this._socket.emit('auth', { token: token })
            })
        });

        this._socket.on('version', (data) => { 
            this.environmentService.importerVersion = data;

            if(this.environmentService.importerVersion != null) {
                console.log('Importer ver. ' + this.environmentService.getVersionReport(this.environmentService.importerVersion))
            }
        });

        this._socket.on('auth', (user) => {
            console.log('Importer auth', user)

            this.status = 'authenticated'
        })

        this._socket.on('error', (error) => {
            console.log('Importer error', error)

            this.status = 'error'
        })

        this._socket.on('disconnect', () => { 
            console.log('Importer disconnect')

            this.status = 'disconnected'

            this.jobs.next(null)
        });

        this._socket.on('message', (data) => { 
            console.log('Importer message', data)
            this.jobs.next(data)
        });
    }

    jobs = new Subject<Array<WSImport.Job>>()

    destroy() {
        this._socket.disconnect()
    }
}

@Injectable({
    providedIn: 'root'
})
export class ImporterService {

    version:{ [service:string]: string } = null;

    constructor(private http:HttpClient, private environmentService:EnvironmentService, private authService:AuthService) {
    }

    hasImporter() {
        return this.environmentService.environment.ImporterURL != null && this.environmentService.environment.ImporterURL != ''
    }

    register() {
        if(this.hasImporter()) {
            return new ImporterRegistration(this.environmentService, this.authService);
        }
    }

    importEntourance(APIUrl:string, items:Array<WSImport.JobEntouranceItem>, realm:string, update?:boolean) {
        return new Observable<void>((observer) => {
            let _request = {
                APIUrl: APIUrl,
                items: items,
                realm: realm,
                update: update
            }

            this.http.post(this.environmentService.environment.ImporterURL + '/import/entourance', _request).subscribe(() => {
                observer.next();
                observer.unsubscribe();
            }, () => {
                observer.error();
                observer.unsubscribe();
            })
        })
    }

    importFile(file:string, fileOrItems:File|Array<any>, resource:string, realm:string, force?:'update'|'insert') {
        return new Observable<void>((observer) => {
            let _request = new FormData()

            if(fileOrItems instanceof File) {
                _request.append('fileToSend', fileOrItems, fileOrItems.name);
            }
            else {
                _request.append('items', JSON.stringify(fileOrItems));
            }
            
            _request.append('file', file);
            _request.append('resource', resource);
            _request.append('realm', realm);
            if(force) _request.append('force', force);

            this.http.post(this.environmentService.environment.ImporterURL + '/import/file', _request).subscribe(() => {
                observer.next();
                observer.unsubscribe();
            }, () => {
                observer.error();
                observer.unsubscribe();
            })
        })
    }

    importWordPress(APIUrl:string, items:Array<WSImport.JobWordPressItem>, realm:string, update?:boolean) {
        return new Observable<void>((observer) => {
            let _request = {
                APIUrl: APIUrl,
                items: items,
                realm: realm,
                update: update
            }

            this.http.post(this.environmentService.environment.ImporterURL + '/import/wordpress', _request).subscribe(() => {
                observer.next();
                observer.unsubscribe();
            }, () => {
                observer.error();
                observer.unsubscribe();
            })
        })
    }

    importDHM(items:Array<WSImport.JobDHMItem>, realm:string, force?:'update'|'update-secondaries') {
        return new Observable<void>((observer) => {
            let _request = {
                items: items,
                realm: realm,
                force: force
            }

            this.http.post(this.environmentService.environment.ImporterURL + '/import/dhm', _request).subscribe(() => {
                observer.next();
                observer.unsubscribe();
            }, () => {
                observer.error();
                observer.unsubscribe();
            })
        })
    }

    importEPPOI(items:Array<WSImport.JobEPPOIItem>, realm:string, force?:'update') {
        return new Observable<void>((observer) => {
            let _request = {
                items: items,
                realm: realm,
                force: force
            }

            this.http.post(this.environmentService.environment.ImporterURL + '/import/eppoi', _request).subscribe(() => {
                observer.next();
                observer.unsubscribe();
            }, () => {
                observer.error();
                observer.unsubscribe();
            })
        })
    }

    checkDHM(items:Array<WSImport.JobDHMItem>, realm:string) {
        return new Observable<WSImport.CheckResult>((observer) => {
            let _request = {
                items: items,
                realm: realm
            }

            this.http.post(this.environmentService.environment.ImporterURL + '/check/dhm', _request).subscribe((data:WSImport.CheckResult) => {
                observer.next(data);
                observer.unsubscribe();
            }, () => {
                observer.error();
                observer.unsubscribe();
            })
        })
    }

    checkEPPOI(items:Array<WSImport.JobEPPOIItem>, realm:string) {
        return new Observable<WSImport.CheckResult>((observer) => {
            let _request = {
                items: items,
                realm: realm
            }

            this.http.post(this.environmentService.environment.ImporterURL + '/check/eppoi', _request).subscribe((data:WSImport.CheckResult) => {
                observer.next(data);
                observer.unsubscribe();
            }, () => {
                observer.error();
                observer.unsubscribe();
            })
        })
    }

    checkEntourance(APIUrl:string, items:Array<WSImport.JobEntouranceItem>, realm:string) {
        return new Observable<WSImport.CheckResult>((observer) => {
            let _request = {
                APIUrl: APIUrl,
                items: items,
                realm: realm
            }

            this.http.post(this.environmentService.environment.ImporterURL + '/check/entourance', _request).subscribe((data:WSImport.CheckResult) => {
                observer.next(data);
                observer.unsubscribe();
            }, () => {
                observer.error();
                observer.unsubscribe();
            })
        })
    }

    checkWordPress(APIUrl:string, items:Array<WSImport.JobWordPressItem>, realm:string) {
        return new Observable<WSImport.CheckResult>((observer) => {
            let _request = {
                APIUrl: APIUrl,
                items: items,
                realm: realm
            }

            this.http.post(this.environmentService.environment.ImporterURL + '/check/wordpress', _request).subscribe((data:WSImport.CheckResult) => {
                observer.next(data);
                observer.unsubscribe();
            }, () => {
                observer.error();
                observer.unsubscribe();
            })
        })
    }

    getCategoriesDHM() {
        return new Observable<{ [id:string]: Array<{ value: string, text: string, data:any }> }>((observer) => {
            this.http.get(this.environmentService.environment.ImporterURL + '/categories/dhm').subscribe((data) => {
                observer.next(data as any);
                observer.unsubscribe();
            }, () => {
                observer.error();
                observer.unsubscribe();
            })
        })
    }

    getCategoriesEPPOI() {
        return new Observable<{ [id:string]: Array<{ value: string, text: string, data?:any }> }>((observer) => {
            this.http.get(this.environmentService.environment.ImporterURL + '/categories/eppoi').subscribe((data) => {
                observer.next(data as any);
                observer.unsubscribe();
            }, () => {
                observer.error();
                observer.unsubscribe();
            })
        })
    }

    getImportTreePagedDirectory(type:string, directory:string, content:'directories'|'files', page:number, params?:{ municipality?:string }) {
        return new Observable<WSImport.TreePagedDirectory>((observer) => {
            let _directory = directory == null || directory === '' ? directory : encodeURIComponent(directory)
            let _url = this.environmentService.environment.ImporterURL + '/tree/' + type + '/' + _directory + '/' + content + '/' + page

            if(params?.municipality) {
                _url += '?municipality=' + encodeURIComponent(params.municipality)
            }

            this.http.get(_url).subscribe((data:any) => {
                observer.next({
                    items: data.items,
                    page: data.page,
                    pageSize: data.pageSize,
                    lastPage: data.lastPage,
                    totalCount: data.totalCount
                })

                observer.unsubscribe();
            }, () => {
                observer.next(null);
                observer.unsubscribe();
            })
        })
    }

    getImportTreePagedSearch(type:string, params:any, page:number) {
        return new Observable<WSImport.TreePagedDirectory>((observer) => {
            let _url = this.environmentService.environment.ImporterURL + '/search/' + type + '/' + encodeURIComponent(JSON.stringify(params))

            this.http.get(_url + '/' + page).subscribe((data:any) => {
                observer.next({
                    items: data.items,
                    page: data.page,
                    pageSize: data.pageSize,
                    lastPage: data.lastPage,
                    totalCount: data.totalCount
                })

                observer.unsubscribe();
            }, () => {
                observer.next(null);
                observer.unsubscribe();
            })
        })
    }

    getImportTemplateUrl(type:string) {
        return this.environmentService.environment.ImporterURL + '/assets/ImportTemplate.' + type
    }

    getError(job:WSImport.Job, progress:number, task:string, index?:number) {
        return new Observable<any>((observer) => {
            if(index != null) task = task + '_' + index;

            this.http.get(this.environmentService.environment.ImporterURL + '/jobs/' + new Date(job.time).toISOString().replace(/:/g, '-').replace(/\./g, '-') + '_' + progress + '_' + task + '.error.json').subscribe((data) => {
                observer.next(data);
                observer.unsubscribe();
            }, () => {
                observer.error();
                observer.unsubscribe();
            })
        })
    }

    getPreviewEntoruance(APIUrl:string, resource:string, id:string) {
        return new Observable<any>((observer) => {
            this.http.get(this.environmentService.environment.ImporterURL + '/preview/entourance/' + encodeURIComponent(APIUrl) + '/' + resource + '/' + id).subscribe((data) => {
                observer.next(data);
                observer.unsubscribe();
            }, () => {
                observer.error();
                observer.unsubscribe();
            })
        })
    }

    getPreviewWordPress(APIUrl:string, resource:string, id:string) {
        return new Observable<any>((observer) => {
            this.http.get(this.environmentService.environment.ImporterURL + '/preview/wordpress/' + encodeURIComponent(APIUrl) + '/' + resource + '/' + id).subscribe((data) => {
                observer.next(data);
                observer.unsubscribe();
            }, () => {
                observer.error();
                observer.unsubscribe();
            })
        })
    }

    getPreviewDHM(resource:string, id:string) {
        return new Observable<any>((observer) => {
            this.http.get(this.environmentService.environment.ImporterURL + '/preview/dhm/' + resource + '/' + id).subscribe((data) => {
                observer.next(data);
                observer.unsubscribe();
            }, () => {
                observer.error();
                observer.unsubscribe();
            })
        })
    }

    getPreviewEPPOI(resource:string, id:string, municipality?:string, page?:number) {
        return new Observable<any>((observer) => {
            let _url = this.environmentService.environment.ImporterURL + '/preview/eppoi/' + resource + '/' + id
            let _params:Array<string> = []

            if(municipality != null && municipality !== '') {
                _params.push('municipality=' + encodeURIComponent(municipality))
            }
            if(page != null) {
                _params.push('page=' + page)
            }
            if(_params.length > 0) {
                _url += '?' + _params.join('&')
            }

            this.http.get(_url).subscribe((data) => {
                observer.next(data);
                observer.unsubscribe();
            }, () => {
                observer.error();
                observer.unsubscribe();
            })
        })
    }

    getLastImportJob(resource:string, id:string) {
        return new Observable<WSImport.Job>((observer) => {
            this.http.get(this.environmentService.environment.ImporterURL + '/jobs/last/' + resource + '/' + id, {
                headers: new HttpHeaders({ 
                    'X-Interceptor-Silent': '404' 
                }),
            }).subscribe((data:WSImport.Job) => {
                observer.next(data);
                observer.unsubscribe();
            }, () => {
                observer.error();
                observer.unsubscribe();
            })
        })
    }

    cancelJob(time:number) {
        return new Observable<WSImport.Job>((observer) => {
            this.http.delete(this.environmentService.environment.ImporterURL + '/jobs/' + time).subscribe((data:WSImport.Job) => {
                observer.next(data);
                observer.unsubscribe();
            }, () => {
                observer.error();
                observer.unsubscribe();
            })
        })
    }

    listFinishedJobs(realm:string, limit:number, offset:number) {
        return new Observable<Array<WSImport.Job>>((observer) => {
            let _params = 'realm=' + realm + '&limit=' + limit + '&offset=' + offset;

            this.http.get(this.environmentService.environment.ImporterURL + '/jobs?' + _params).subscribe((data:Array<WSImport.Job>) => {
                observer.next(data);
                observer.unsubscribe();
            }, () => {
                observer.error();
                observer.unsubscribe();
            })
        })
    }
}
